import React, { useState, useEffect } from 'react';
import './App.css';
import StoreLogin from './components/StoreLogin';
import StoreDashboard from './components/StoreDashboard';
import OrderManagement from './components/OrderManagement';
import MenuManagement from './components/MenuManagement';
import StoreSettings from './components/StoreSettings';
import EarningsDashboard from './components/EarningsDashboard';
import LocationPermission from './components/LocationPermission';
import storeService from './services/storeService';

type View = 'dashboard' | 'orders' | 'menu' | 'earnings' | 'settings';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [storeData, setStoreData] = useState<any>(null);
  const [currentView, setCurrentView] = useState<View>('dashboard');
  const [isStoreOpen, setIsStoreOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [locationGranted, setLocationGranted] = useState(false);
  const [storeLocation, setStoreLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const handleLocationGranted = (location: { lat: number; lng: number }) => {
    setStoreLocation(location);
    setLocationGranted(true);
  };

  const checkAuth = () => {
    const token = localStorage.getItem('storeToken');
    const store = localStorage.getItem('storeData');
    
    if (token && store) {
      setIsAuthenticated(true);
      setStoreData(JSON.parse(store));
      setIsStoreOpen(JSON.parse(store).isOpen || false);
    }
    setLoading(false);
  };

  const handleLogin = (token: string, store: any) => {
    localStorage.setItem('storeToken', token);
    localStorage.setItem('storeData', JSON.stringify(store));
    setStoreData(store);
    setIsAuthenticated(true);
    setIsStoreOpen(store.isOpen || false);
  };

  const handleLogout = () => {
    localStorage.removeItem('storeToken');
    localStorage.removeItem('storeData');
    setIsAuthenticated(false);
    setStoreData(null);
    setCurrentView('dashboard');
  };

  const toggleStoreStatus = async () => {
    try {
      const newStatus = !isStoreOpen;
      await storeService.updateStoreStatus(storeData._id, newStatus);
      setIsStoreOpen(newStatus);
      
      // Update local storage
      const updatedStore = { ...storeData, isOpen: newStatus };
      setStoreData(updatedStore);
      localStorage.setItem('storeData', JSON.stringify(updatedStore));
    } catch (error) {
      console.error('Error toggling store status:', error);
      alert('Failed to update store status');
    }
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Show location permission screen first
  if (!locationGranted) {
    return <LocationPermission onLocationGranted={handleLocationGranted} />;
  }

  if (!isAuthenticated) {
    return (
      <div className="App">
        <StoreLogin onLogin={handleLogin} />
      </div>
    );
  }

  const renderView = () => {
    switch (currentView) {
      case 'dashboard':
        return <StoreDashboard storeData={storeData} onNavigate={setCurrentView} />;
      case 'orders':
        return <OrderManagement storeData={storeData} />;
      case 'menu':
        return <MenuManagement storeData={storeData} />;
      case 'earnings':
        return <EarningsDashboard storeData={storeData} />;
      case 'settings':
        return <StoreSettings storeData={storeData} onUpdate={setStoreData} />;
      default:
        return <StoreDashboard storeData={storeData} onNavigate={setCurrentView} />;
    }
  };

  return (
    <div className="App">
      {/* Header */}
      <header className="store-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-container">
              <img src="/logo-new.png" alt="BringIt" className="logo-image" />
            </div>
            <div>
              <h1 className="app-title">BringIt</h1>
              <span className="store-badge">Store Panel</span>
            </div>
          </div>

          <div className="header-actions">
            <div className="store-status-toggle" onClick={toggleStoreStatus} style={{ cursor: 'pointer' }}>
              <span className={`status-indicator ${isStoreOpen ? '' : 'closed'}`}></span>
              <span>{isStoreOpen ? 'Store Open' : 'Store Closed'}</span>
            </div>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div style={{
        background: 'rgba(139, 92, 246, 0.1)',
        borderBottom: '1px solid rgba(139, 92, 246, 0.3)',
        padding: '10px 0',
        position: 'sticky',
        top: '70px',
        zIndex: 99
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          gap: '10px',
          padding: '0 20px',
          overflowX: 'auto'
        }}>
          {[
            { key: 'dashboard', label: '📊 Dashboard', icon: '📊' },
            { key: 'orders', label: '📦 Orders', icon: '📦' },
            { key: 'menu', label: '🍽️ Menu', icon: '🍽️' },
            { key: 'earnings', label: '💰 Earnings', icon: '💰' },
            { key: 'settings', label: '⚙️ Settings', icon: '⚙️' }
          ].map(nav => (
            <button
              key={nav.key}
              onClick={() => setCurrentView(nav.key as View)}
              style={{
                background: currentView === nav.key 
                  ? 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'
                  : 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '20px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}
            >
              {nav.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="main-content">
        {renderView()}
      </main>
    </div>
  );
}

export default App;
